import React from "react";
import { CheckCircle2, ClipboardList, UserCheck, ShieldCheck, Smile } from "lucide-react";
import { motion } from "framer-motion";

const steps = [
  {
    step: "01",
    title: "Select & Submit Request",
    desc: "Choose your required service (Property Management, e-Khata, Painting, Agreement, Furniture) and share your property details online.",
    icon: ClipboardList,
  },
  {
    step: "02",
    title: "Personal Manager Assigned",
    desc: "A dedicated real estate specialist connects with you in 15 minutes to understand your exact expectations and provide transparent quotation.",
    icon: UserCheck,
  },
  {
    step: "03",
    title: "Verified Execution & Legal Check",
    desc: "Our field team, legal advisors, and vetted technicians execute the service with strict quality compliance, background checks, and digital tracking.",
    icon: ShieldCheck,
  },
  {
    step: "04",
    title: "Handover & Ongoing Support",
    desc: "Receive digital certificates, stamped agreements, or inspected keys directly at your doorstep with 100% satisfaction guarantee.",
    icon: Smile,
  },
];

const ServicesProcess = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-12 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-50 text-red-600 border border-red-100 text-xs font-bold uppercase tracking-wider mb-4">
            How It Works
          </span>
          <h2 className="text-3xl sm:text-5xl font-black text-gray-900 tracking-tight">
            Seamless Execution from <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-rose-500">
              Start to Delivery
            </span>
          </h2>
          <p className="mt-4 text-gray-600 text-base sm:text-lg">
            Say goodbye to multiple vendor calls and unreliable contractors.
            Experience our predictable 4-step workflow tailored for modern property owners.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {steps.map((item, idx) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
                className="relative bg-gradient-to-b from-gray-50 to-white border border-gray-200/80 rounded-3xl p-7 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-4xl font-black text-gray-200 font-mono">
                      {item.step}
                    </span>
                    <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-600 border border-red-100 flex items-center justify-center">
                      <Icon className="w-6 h-6" />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>

                  <p className="text-sm text-gray-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-100 flex items-center gap-2 text-xs font-bold text-red-600">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span>Guaranteed Standards</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesProcess;
